import {
  CreateOrderState,
  createOrderState,
} from "@/atoms/order/create-order-state";
import { PayMethod } from "@/common/enum/pay-method";
import { validateEmailId } from "@/common/validator/validate-email-id";
import { validatePhoneNumber } from "@/common/validator/validate-phone-number";
import { PartnerPaymentButton } from "@/components/button/PartnerPaymentButton";
import { PartnerCreateOrderHeader } from "@/components/header/PartnerCreateOrderHeader";
import { PartnerDeliveryAddressInputPanel } from "@/components/panel/PartnerDeliveryAddressInputPanel";
import { PartnerPaymentTypePanel } from "@/components/panel/PartnerPaymentTypePanel";
import { PartnerOrderDetailOrdererTable } from "@/components/table/partner-order-orderer-table/PartnerOrderDetailOrdererTable";
import { H5 } from "@/components/typography/H5";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { orderService } from "@/services/order-service2";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { PartnerOrderItemTable } from "../../../components/table/partner-order-item-table/PartnerOrderItemTable";
import PayLaterConfirm from "./_componant/PayLaterConfirm";
import VirtualAccountInput, {
  ReceiptState,
} from "./_componant/VirtualAccountInput";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import { Body16Medium } from "@/components/typography/Body16Medium";

export interface CreatePartnerOrderStateError {
  phone: string;
  emailId: string;
}

// 결제하기 페이지
export default function PartnerCreateOrderPage() {
  const [paymentType, setPaymentType] = useState<PayMethod>(
    PayMethod.CREDIT_CARD,
  );
  const [receiptState, setReceiptState] = useState<ReceiptState>({
    isApplied: true,
    type: "business",
    taxNumber: "",
  });
  const [isPayLaterConfirmed, setIsPayLaterConfirmed] = useState(false);
  const [filteredInvoiceList, setFilteredInvoiceList] = useState<
    OrderDetailDto[]
  >([]);
  const payload = useRecoilValue<CreateOrderState>(createOrderState);
  const [aggregateErrors, setAggregateErrors] =
    useState<CreatePartnerOrderStateError>({
      phone: "",
      emailId: "",
    });

  const handlePaymentButtonClick = () => {
    const errors: CreatePartnerOrderStateError = {
      phone: validatePhoneNumber(payload.receiverPhone)
        ? ""
        : "올바르지 않은 번호 유형입니다.",
      emailId: validateEmailId(payload.receiverEmailId)
        ? ""
        : "영문의 이메일 아이디를 입력해주세요.",
    };

    setAggregateErrors(errors);

    if (Object.values(errors).some((error) => error)) {
      return;
    }

    // TODO 결제 API 호출
  };
  useEffect(() => {
    const fetch = async () => {
      const { data, ok } = await orderService.getInvoiceList({
        paymentType: "PAVC",
        paymentStatus: "PENDING",
      });
      if (ok) {
        setFilteredInvoiceList(data);
      }
    };

    fetch();
  }, []);

  useEffect(() => {
    setIsPayLaterConfirmed(false);
  }, [paymentType]);

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1392px] h-full items-between justify-start gap-[24px]">
        <PartnerCreateOrderHeader
          quote={{
            id: 1,
            name: "견적서 상세 정보",
          }}
        />

        <div className="flex flex-col">
          <H5 className="text-semantic-text-heading py-[8px]">발주 정보</H5>
          <PartnerOrderDetailOrdererTable />
        </div>

        <div>
          <div className="h-[40px] py-[8px] flex items-center gap-[12px]">
            <H5 className="text-semantic-text-heading">발주 상품</H5>
          </div>
          <PartnerOrderItemTable />
        </div>

        <div>
          <div className="h-[40px] py-[8px] flex items-center gap-[12px]">
            <H5 className="text-semantic-text-heading">배송지 정보</H5>
          </div>
          <PartnerDeliveryAddressInputPanel aggregateErrors={aggregateErrors} />
        </div>
        <div>
          <div className="h-[40px] py-[8px] flex items-center gap-[12px]">
            <H5 className="text-semantic-text-heading">결제 정보</H5>
          </div>
          <PartnerPaymentTypePanel
            paymentType={paymentType}
            setPaymentType={setPaymentType}
            onPayLaterConfirm={(v) => setIsPayLaterConfirmed(v)}
          >
            <>
              {paymentType === PayMethod.VIRTUAL_ACCOUNT && (
                <>
                  <div className="text-[#E80A4D] flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 15.5C6.51664 15.5 5.06659 15.0601 3.83323 14.236C2.59986 13.4119 1.63856 12.2406 1.07091 10.8701C0.50325 9.49968 0.354725 7.99168 0.644114 6.53683C0.933503 5.08197 1.64781 3.7456 2.6967 2.6967C3.7456 1.64781 5.08197 0.933503 6.53683 0.644114C7.99168 0.354725 9.49968 0.50325 10.8701 1.07091C12.2406 1.63856 13.4119 2.59986 14.236 3.83323C15.0601 5.06659 15.5 6.51664 15.5 8C15.4978 9.98845 14.7069 11.8948 13.3009 13.3009C11.8948 14.7069 9.98845 15.4978 8 15.5ZM8 2C6.81331 2 5.65328 2.3519 4.66658 3.01119C3.67989 3.67047 2.91085 4.60755 2.45673 5.7039C2.0026 6.80026 1.88378 8.00666 2.11529 9.17054C2.3468 10.3344 2.91825 11.4035 3.75736 12.2426C4.59648 13.0818 5.66558 13.6532 6.82946 13.8847C7.99335 14.1162 9.19975 13.9974 10.2961 13.5433C11.3925 13.0892 12.3295 12.3201 12.9888 11.3334C13.6481 10.3467 14 9.18669 14 8C13.9982 6.40925 13.3655 4.88417 12.2407 3.75934C11.1158 2.6345 9.59075 2.00179 8 2Z" fill="#E80A4D" />
                      <path d="M8 9.5C7.80109 9.5 7.61032 9.42098 7.46967 9.28033C7.32902 9.13968 7.25 8.94891 7.25 8.75V5C7.25 4.80109 7.32902 4.61032 7.46967 4.46967C7.61032 4.32902 7.80109 4.25 8 4.25C8.19891 4.25 8.38968 4.32902 8.53033 4.46967C8.67098 4.61032 8.75 4.80109 8.75 5V8.75C8.75 8.94891 8.67098 9.13968 8.53033 9.28033C8.38968 9.42098 8.19891 9.5 8 9.5Z" fill="#E80A4D" />
                      <path d="M8 11.75C8.41422 11.75 8.75 11.4142 8.75 11C8.75 10.5858 8.41422 10.25 8 10.25C7.58579 10.25 7.25 10.5858 7.25 11C7.25 11.4142 7.58579 11.75 8 11.75Z" fill="#E80A4D" />
                    </svg>
                    <Body16Medium>
                      {new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })} 이내 입금확인이 되지 않으면 발주가 자동 취소 처리됩니다.
                    </Body16Medium>
                  </div>
                  <VirtualAccountInput
                    receiptState={receiptState}
                    setReceiptState={setReceiptState}
                  />
                </>
              )}
              {paymentType === PayMethod.PAY_LATER && (
                <PayLaterConfirm
                  setIsPayLaterConfirmed={setIsPayLaterConfirmed}
                />
              )}
              {paymentType === PayMethod.BANK_TRANSFER && (
                <VirtualAccountInput
                  receiptState={receiptState}
                  setReceiptState={setReceiptState}
                />
              )}
            </>
          </PartnerPaymentTypePanel>

          <PartnerPaymentButton
            payMethod={paymentType}
            disabled={
              (paymentType === PayMethod.PAY_LATER && !isPayLaterConfirmed) ||
              (paymentType === PayMethod.VIRTUAL_ACCOUNT &&
                filteredInvoiceList.length > 0)
            }
            receiptState={receiptState}
          />
        </div>
      </div>
    </PageWrapper>
  );
}
